/**
 * @file 채널 폴더 관리 — 생성·이름/설명 수정·순서·삭제 + <b>폴더 참가자</b>. PM·채널 관리자 전용.
 *
 * <h3>무엇이 즉시 저장되고 무엇이 '저장' 버튼을 기다리나</h3>
 * <p>섞여 있으면 사용자가 "지금 반영된 건가" 를 매번 의심하게 되므로 기준을 하나로 뒀다 —
 * <b>구조를 바꾸는 것은 즉시, 글자를 고치는 것은 저장 버튼.</b>
 * <ul>
 *   <li>즉시 — 폴더 추가, 순서(↑↓), 삭제, 참가자 추가·제거. 모두 그 자리에서 결과가 보이는 동작이다.</li>
 *   <li>저장 버튼 — 폴더명·설명. 타이핑 중간값이 서버로 새어 나가지 않게 모아서 보낸다.</li>
 * </ul>
 *
 * <h3>참가자가 왜 여기 있나</h3>
 * <p>폴더는 참가자만 보고 쓴다. 폴더를 만들자마자 참가자를 지정하지 않으면 <b>PM 말고 아무도 못 보는
 * 폴더</b>가 되므로, 폴더 줄을 펼치면 바로 참가자를 넣을 수 있게 같은 화면에 뒀다.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete, Box, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ActionButton from "../../../component/common/ActionButton";
import FormField from "../../../component/common/FormField";
import { channelApi } from "../api/channelApi";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";
import { runApiAction, getApiErrorMessage, serverMsgOr } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

/** 채널 참가자 응답({ id: 멤버십PK, employee: {...} })에서 직원 객체를 꺼낸다. */
const empOf = (m) => m?.employee ?? m;

export default function FolderManageDialog({
    open, channelId, folders = [], channelMembers = [], onClose, onChanged,
}) {
    const [rows, setRows] = useState([]);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    /**
     * 참가자 줄을 펼친 폴더 id 집합. <b>기본은 전부 접힘</b>이고 새로 만든 폴더만 열린다.
     *
     * <p>한때 "참가자 0명이면 강제로 열어 둔다" 로 했다가 되돌렸다 — 빈 폴더가 4개만 되어도
     * 참가자 영역이 다 펼쳐져 다이얼로그가 화면을 한참 넘어갔다. 대신 접힌 요약 줄에
     * <b>주황색 경고</b>를 남겨 놓쳐도 티가 나게 한다.
     */
    const [expandedIds, setExpandedIds] = useState(new Set());
    const toggleExpanded = (id) => setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const isEditorOpen = (row) => expandedIds.has(row.id);

    /**
     * 서버 폴더 목록 → 편집 사본 동기화.
     *
     * <p>참가자를 추가하면 {@code onChanged} 로 목록을 다시 읽으므로 이 effect 가 <b>매번</b> 돈다.
     * 예전엔 여기서 {@code setExpandedId(null)} 까지 했더니, 참가자 한 명을 넣을 때마다
     * 참가자 줄이 접혀 <b>연속으로 추가할 수가 없었다</b>. 펼침 상태는 목록 데이터와 무관한
     * 화면 상태라 여기서 건드리면 안 된다.
     *
     * <p>{@code base} 는 서버 원본 — '저장' 이 <b>바뀐 줄만</b> 보내기 위한 비교 기준이다.
     */
    useEffect(() => {
        if (!open) return;
        setRows(folders.map(f => ({
            id: f.id,
            name: f.name,
            description: f.description || "",
            feedCount: f.feedCount,
            memberCount: f.memberCount,
            base: { name: f.name, description: f.description || "" },
        })));
    }, [open, folders]);

    /** 다이얼로그를 <b>열 때만</b> 초기화 — 입력 중이던 값·펼침 상태가 갱신 때마다 날아가지 않게. */
    useEffect(() => {
        if (!open) return;
        setNewName("");
        setExpandedIds(new Set());
    }, [open]);

    /** 저장할 것이 있는지 — 없으면 '저장' 을 비활성화해 헛클릭을 막는다. */
    const dirtyRows = useMemo(
        () => rows.filter(r => r.name.trim() !== r.base.name || (r.description || "") !== r.base.description),
        [rows]);

    const handleCreate = async () => {
        const name = newName.trim();
        if (!name) { notify("폴더명을 입력하세요", "error"); return; }
        setCreating(true);
        try {
            const created = await channelApi.createFolder(channelId, { name });
            setNewName("");
            onChanged();
            // 만든 직후 참가자 줄을 펼친다 — 참가자를 안 넣으면 PM 만 보이는 폴더가 되기 때문.
            // (0명이라 어차피 강제 열림이지만, 명시해 두면 참가자를 넣은 뒤에도 열린 채 남는다.)
            if (created?.id) setExpandedIds(prev => new Set(prev).add(created.id));
        } catch (err) {
            notify(getApiErrorMessage(err, "폴더 생성 실패"), "error");
        } finally {
            setCreating(false);
        }
    };

    /** 바뀐 줄만 순차 저장. 하나가 실패해도 나머지는 반영하고, 실패한 폴더명을 알려준다. */
    const handleSaveAll = async () => {
        if (dirtyRows.length === 0) { onClose(); return; }
        const blank = dirtyRows.find(r => !r.name.trim());
        if (blank) { notify("폴더명은 비워 둘 수 없습니다", "error"); return; }

        setSaving(true);
        const failed = [];
        for (const r of dirtyRows) {
            try {
                await channelApi.updateFolder(r.id, {
                    name: r.name.trim(),
                    description: r.description || null,
                });
            } catch (err) {
                failed.push(`${r.name}(${getApiErrorMessage(err, "실패")})`);
            }
        }
        setSaving(false);
        onChanged();

        if (failed.length) notify(`저장 실패: ${failed.join(", ")}`, "error");
        else { notify("폴더를 저장했습니다", "success"); onClose(); }
    };

    /** 위/아래 이동 — 화면 순서를 바꾼 뒤 그 순서를 그대로 서버에 저장한다(즉시). */
    const move = (index, delta) => {
        const next = [...rows];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setRows(next);
        return runApiAction({
            request: () => channelApi.reorderFolders(channelId, next.map(r => r.id)),
            errorMsg: serverMsgOr("순서 저장 실패"),
            silent: true,
            onSuccess: onChanged,
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>폴더 관리</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: 12.5, color: C.textMuted, mb: 2 }}>
                    한 행사·프로젝트를 부서나 섹션 단위로 나눕니다. 폴더의 글·일정·업무는
                    <Box component="span" sx={{ fontWeight: 700, color: C.textSecondary }}> 그 폴더 참가자만 </Box>
                    보고 쓸 수 있습니다(PM·관리자는 전체 열람).
                </Typography>

                <FormField label="새 폴더" required>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small" fullWidth
                            placeholder="예: 디자인팀, 무대 운영"
                            value={newName}
                            onChange={e => setNewName(e.target.value.slice(0, 100))}
                            /* isComposing — 한글 조합 중 Enter 로 폴더가 두 개 만들어지는 것 방지
                               (ChannelPage 의 폴더 입력과 같은 사유) */
                            onKeyDown={e => {
                                if (e.nativeEvent?.isComposing) return;
                                if (e.key === "Enter") handleCreate();
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
                        />
                        <ActionButton variant="add"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={handleCreate} loading={creating}
                            sx={{ fontSize: 12.5, flexShrink: 0 }} label="추가" />
                    </Stack>
                </FormField>

                {rows.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                            아직 폴더가 없습니다. 위에서 첫 폴더를 만들어 보세요.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.25} mt={1}>
                        {rows.map((row, i) => (
                            <Box key={row.id} sx={{
                                // 회색 채움 대신 <b>테두리</b>로 영역을 잡는다 — 안쪽 입력칸이 흰색이라
                                // 배경까지 회색이면 두 톤이 겹쳐 탁해 보인다(사내 톤 = 그림자 대신 테두리).
                                border: `1px solid ${C.border}`, borderRadius: 1.5,
                                bgcolor: C.surface, px: 1.5, py: 1.25,
                            }}>
                                {/*
                                  * 카드 헤더 — 접힌 상태에서 보이는 유일한 줄.
                                  * 폴더명 + 참가자 인원만 남긴다(그 둘이 "손볼 폴더인지" 판단에 필요한 전부다).
                                  * 접기/펼치기는 <b>카드 하나에 하나</b>만 둔다 — 카드 접힘과 참가자 접힘을
                                  * 따로 두면 2단이 되어 "무엇이 접힌 건지" 를 사용자가 추적해야 한다.
                                  */}
                                <Stack direction="row" alignItems="center" spacing={0.75}
                                    onClick={() => toggleExpanded(row.id)}
                                    sx={{ cursor: "pointer", "&:hover .folder-card-name": { color: C.primary } }}>
                                    <FolderRoundedIcon sx={{ fontSize: 17, color: "#f59e0b", flexShrink: 0 }} />
                                    <Typography noWrap className="folder-card-name" sx={{
                                        fontSize: 13.5, fontWeight: 700, color: C.textPrimary,
                                        transition: "color .12s", minWidth: 0,
                                    }}>
                                        {row.base.name || "새 폴더"}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: 12, flexShrink: 0,
                                        fontWeight: (row.memberCount ?? 0) === 0 ? 700 : 500,
                                        color: (row.memberCount ?? 0) === 0 ? C.warning : C.textMuted,
                                    }}>
                                        참가자 {row.memberCount ?? 0}명
                                    </Typography>
                                    {(row.memberCount ?? 0) === 0 && !isEditorOpen(row) && (
                                        <Typography noWrap sx={{ fontSize: 11.5, color: C.warning, minWidth: 0 }}>
                                            — 추가해야 PM·관리자 외에도 보입니다
                                        </Typography>
                                    )}
                                    <Box flex={1} />
                                    <ExpandLessRoundedIcon sx={{
                                        fontSize: 17, color: C.textMuted, flexShrink: 0,
                                        transition: "transform 160ms ease",
                                        transform: isEditorOpen(row) ? "none" : "rotate(180deg)",
                                    }} />
                                    {/* 순서·삭제는 접혀 있어도 쓸 수 있어야 한다(정리 작업의 대부분이 이 둘이다).
                                        카드 클릭이 접기라서 버블링을 끊는다. */}
                                    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}
                                        onClick={e => e.stopPropagation()}>
                                        <Tooltip title="위로">
                                            <span>
                                                <IconButton size="small" onClick={() => move(i, -1)}
                                                    disabled={i === 0} sx={{ p: 0.4 }}>
                                                    <ArrowUpwardRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="아래로">
                                            <span>
                                                <IconButton size="small" onClick={() => move(i, 1)}
                                                    disabled={i === rows.length - 1} sx={{ p: 0.4 }}>
                                                    <ArrowDownwardRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <DeleteFolderButton
                                            folder={row}
                                            otherFolders={rows.filter(r => r.id !== row.id)}
                                            onDeleted={onChanged}
                                        />
                                    </Stack>
                                </Stack>

                                {/* 펼친 내용 — 입력칸 + 참가자 편집기. 안쪽에 또 다른 토글은 두지 않는다. */}
                                <Collapse in={isEditorOpen(row)} unmountOnExit>
                                    <Box sx={{ pt: 1.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-end">
                                            <FormField label="폴더명" required sx={{ flex: 1, minWidth: 0 }}>
                                                <TextField
                                                    size="small" fullWidth
                                                    value={row.name}
                                                    onChange={e => setRows(p => p.map((r, j) =>
                                                        j === i ? { ...r, name: e.target.value.slice(0, 100) } : r))}
                                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
                                                />
                                            </FormField>
                                            <FormField label="설명" sx={{ flex: 1.4, minWidth: 0 }}>
                                                <TextField
                                                    size="small" fullWidth
                                                    value={row.description}
                                                    onChange={e => setRows(p => p.map((r, j) =>
                                                        j === i ? { ...r, description: e.target.value.slice(0, 300) } : r))}
                                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5 } }}
                                                />
                                            </FormField>
                                        </Stack>

                                        <Stack direction="row" alignItems="center" spacing={0.5} mt={1.25}>
                                            <GroupRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary }}>
                                                참가자
                                            </Typography>
                                            {row.feedCount > 0 && (
                                                <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>
                                                    · 글 {row.feedCount}건
                                                </Typography>
                                            )}
                                        </Stack>
                                        <FolderMemberEditor
                                            folderId={row.id}
                                            channelMembers={channelMembers}
                                            onChanged={onChanged}
                                        />
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {/* 닫기는 회색(중립) — 파란 채움은 '저장' 하나만 갖는다.
                    둘 다 파랗면 어느 쪽이 반영되는 버튼인지 눈으로 구분할 수 없다. */}
                <ActionButton variant="cancel" onClick={onClose} disabled={saving} label="닫기" />
                <ActionButton variant="save" onClick={handleSaveAll} loading={saving}
                    disabled={dirtyRows.length === 0}
                    label={dirtyRows.length > 0 ? `저장 (${dirtyRows.length})` : "저장"} />
            </DialogActions>
        </Dialog>
    );
}

/**
 * 폴더 삭제 — <b>내용물을 어디로 옮길지 먼저 고른다.</b>
 *
 * <p>옮긴 항목은 대상 폴더 참가자에게 보이게 되므로, 삭제는 분류 변경이면서 가시성 변경이다.
 * 그래서 대상을 고르는 단계를 건너뛸 수 없게 셀렉트를 앞에 둔다.
 */
function DeleteFolderButton({ folder, otherFolders, onDeleted }) {
    const [open, setOpen] = useState(false);
    const [target, setTarget] = useState("");   // "" = 미분류
    const [deleting, setDeleting] = useState(false);

    const hasContent = (folder.feedCount ?? 0) > 0;

    const handleDelete = async () => {
        const targetName = target === ""
            ? "미분류"
            : (otherFolders.find(f => String(f.id) === String(target))?.name ?? "선택한 폴더");
        const msg = hasContent
            ? `'${folder.name}' 폴더를 삭제하시겠습니까?\n안의 항목은 삭제되지 않고 '${targetName}'(으)로 옮겨집니다.`
            : `'${folder.name}' 폴더를 삭제하시겠습니까?`;
        if (!(await confirmDialog({ message: msg }))) return;
        return runApiAction({
            request: () => channelApi.deleteFolder(folder.id, target === "" ? undefined : Number(target)),
            successMsg: `폴더를 삭제하고 항목을 '${targetName}'(으)로 옮겼습니다`,
            errorMsg: serverMsgOr("폴더 삭제 실패"),
            setLoading: setDeleting,
            onSuccess: () => { setOpen(false); onDeleted(); },
        });
    };

    if (!open) {
        return (
            <Tooltip title="삭제">
                <IconButton size="small" onClick={() => setOpen(true)} sx={{ p: 0.4 }}>
                    <DeleteRoundedIcon sx={{ fontSize: 16, color: C.error }} />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Select size="small" value={target} onChange={e => setTarget(e.target.value)}
                displayEmpty
                sx={{ fontSize: 12, minWidth: 118, bgcolor: C.surface }}>
                <MenuItem value="" sx={{ fontSize: 12.5 }}>미분류로 이동</MenuItem>
                {otherFolders.map(f => (
                    <MenuItem key={f.id} value={f.id} sx={{ fontSize: 12.5 }}>{f.name}(으)로</MenuItem>
                ))}
            </Select>
            <ActionButton variant="delete" onClick={handleDelete} loading={deleting}
                startIcon={false} sx={{ fontSize: 11.5, py: 0.2, minWidth: 0 }} label="삭제" />
            <ActionButton variant="cancel" onClick={() => setOpen(false)}
                sx={{ fontSize: 11.5, py: 0.2, minWidth: 0 }} />
        </Stack>
    );
}

/** 폴더 참가자 편집 — 채널 참가자 중에서만 고를 수 있다(서버도 같은 규칙을 강제). 추가·제거는 즉시 반영. */
function FolderMemberEditor({ folderId, channelMembers, onChanged }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [picked, setPicked] = useState([]);
    const [adding, setAdding] = useState(false);
    const mounted = useRef(true);

    useEffect(() => () => { mounted.current = false; }, []);

    const load = () => {
        setLoading(true);
        channelApi.getFolderMembers(folderId)
            .then(r => { if (mounted.current) setMembers(Array.isArray(r) ? r : (r?.data ?? [])); })
            .catch(() => { if (mounted.current) setMembers([]); })
            .finally(() => { if (mounted.current) setLoading(false); });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [folderId]);

    const memberEmpIds = new Set(members.map(m => m.employee?.id));
    /** 아직 이 폴더에 없는 채널 참가자만 후보로 — 이미 있는 사람을 다시 고르게 하지 않는다. */
    const candidates = channelMembers
        .map(empOf)
        .filter(e => e?.id && !memberEmpIds.has(e.id));

    const handleAdd = async () => {
        if (picked.length === 0) return;
        setAdding(true);
        try {
            await channelApi.addFolderMembers(folderId, picked.map(p => p.id));
            setPicked([]);
            load();
            onChanged();
        } catch (err) {
            notify(getApiErrorMessage(err, "참가자 추가 실패"), "error");
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = (employeeId) => runApiAction({
        request: () => channelApi.removeFolderMember(folderId, employeeId),
        errorMsg: serverMsgOr("참가자 제거 실패"),
        silent: true,
        onSuccess: () => { load(); onChanged(); },
    });

    return (
        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${C.borderLight}` }}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <Autocomplete
                    multiple size="small" fullWidth
                    options={candidates}
                    value={picked}
                    onChange={(_, v) => setPicked(v)}
                    getOptionLabel={(e) => `${e.name}${e.positionName ? ` ${e.positionName}` : ""}`}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    noOptionsText="추가할 채널 참가자가 없습니다"
                    renderInput={(p) => (
                        <TextField {...p} placeholder={candidates.length ? "채널 참가자에서 선택" : "후보 없음"}
                            sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5 } }} />
                    )}
                />
                <ActionButton variant="add" startIcon={false}
                    onClick={handleAdd} loading={adding} disabled={picked.length === 0}
                    sx={{ fontSize: 11.5, py: 0.4, flexShrink: 0 }} label="추가" />
            </Stack>

            {!loading ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.75}>
                    {members.map(m => (
                        <Chip key={m.id}
                              label={`${m.employee?.name ?? "?"}${m.employee?.positionName ? ` ${m.employee.positionName}` : ""}`}
                              size="small"
                              onDelete={() => handleRemove(m.employee?.id)}
                              sx={{ height: 24, fontSize: 12, bgcolor: C.bg, border: `1px solid ${C.border}` }}
                        />
                    ))}
                </Stack>
            ) : (
                <>
                </>
            )}
        </Box>
    );
}
